import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Content, Header, Page, Progress, StatusError, StatusOK, InfoCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { tenantApiRef } from '../api';
import { Button, TextField, Grid, Tabs, Tab, Box, Typography, Paper, FormControlLabel, Switch, MenuItem, Select, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteIcon from '@material-ui/icons/Delete';
import ArchiveIcon from '@material-ui/icons/Archive';

// Tenant teardown runs in deliver-different/tenant-ops. Backstage does not start
// it: the button opens GitLab's Run pipeline form, so the person running it is
// the pipeline's starter and a second person has to approve every step (a
// pipeline started by Backstage's bot would let the requester approve it).
const TENANT_OPS_RUN_URL = 'https://gitlab.com/deliver-different/tenant-ops/-/pipelines/new';

// CONFIRM_TENANT is left empty on purpose: typing the name again is the check.
const teardownRunUrl = (shortName: string, dryRun: boolean) => {
  const vars: [string, string][] = [
    ['TENANT', shortName],
    ['CONFIRM_TENANT', ''],
    ...(dryRun ? [['PHASE_A_DRY_RUN', '1'], ['PHASE_B_DRY_RUN', '1']] as [string, string][] : []),
  ];
  return `${TENANT_OPS_RUN_URL}?ref=main&${vars.map(([k, v]) => `var[${k}]=${encodeURIComponent(v)}`).join('&')}`;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

export const TenantViewPage = () => {
  const { short_name } = useParams<{ short_name: string }>();
  const tenantApi = useApi(tenantApiRef);
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [editMode, setEditMode] = useState(false);
  const [editedTenant, setEditedTenant] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [appInput, setAppInput] = useState('');
  const [newAppInput, setNewAppInput] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<'idle' | 'deleting' | 'error'>('idle');
  const [decommissionDialogOpen, setDecommissionDialogOpen] = useState(false);
  const [decommissionDryRun, setDecommissionDryRun] = useState(true);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        if (short_name) {
          const data = await tenantApi.getTenantByShortName(short_name);
          setTenant(data);
          setEditedTenant(JSON.parse(JSON.stringify(data))); // Create a deep copy
        } else {
          setError(new Error('Tenant short name is undefined'));
        }
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [short_name, tenantApi]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditToggle = () => {
    if (editMode) {
      // If we're exiting edit mode without saving, reset to original
      setEditedTenant(JSON.parse(JSON.stringify(tenant)));
    }
    setEditMode(!editMode);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    if (!short_name || !editedTenant) return;
    
    try {
      setSaveStatus('saving');
      
      // Only send fields that can be updated
      const updateData = {
        name: editedTenant.name,
        apps: editedTenant.apps,
        profile: editedTenant.profile,
        address: editedTenant.address,
        billing: editedTenant.billing,
        settings: editedTenant.settings,
        feature_gates: editedTenant.feature_gates,
        branding: editedTenant.branding,
        // infrastructure: editedTenant.infrastructure,
      };
      
      const updatedTenant = await tenantApi.updateTenant(short_name, updateData);
      setTenant(updatedTenant);
      console.log('Updated tenant:', updatedTenant);
      setEditedTenant(JSON.parse(JSON.stringify(updatedTenant)));
      setEditMode(false);
      setSaveStatus('success');
      
      // Reset success status after a delay
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (err) {
      setError(err as Error);
      setSaveStatus('error');
    }
  };

  const handleDelete = async () => {
    if (!short_name) return;
    
    try {
      setDeleteStatus('deleting');
      await tenantApi.deleteTenant(short_name);
      setDeleteDialogOpen(false);
      navigate('/tenants'); // Redirect to tenant list
    } catch (err) {
      setError(err as Error);
      setDeleteStatus('error');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setEditedTenant((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (section: string, field: string, value: any) => {
    setEditedTenant((prev: any) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value,
      },
    }));
  };

  if (loading) return <Progress />;
  if (error)
    return (
      <StatusError>{error.message}</StatusError>
    );

  if (!tenant)
    return (
      <StatusError>No tenant found for short name "${short_name}"</StatusError>
    );

  return (
    <Page themeId="tool">
      <Header 
        title={`Tenant: ${tenant.name}`} 
        subtitle={tenant.short_name}
      />
      <Content>
        {saveStatus === 'success' && (
          <StatusOK>Changes saved successfully!</StatusOK>
        )}
        {saveStatus === 'error' && (
          <StatusError>Error: {error}</StatusError>
        )}
        
        {/* Add explicit edit button at the top of content */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={editMode}
            >
              Delete Tenant
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={() => { setDecommissionDryRun(true); setDecommissionDialogOpen(true); }}
              disabled={editMode}
              style={{ marginLeft: '8px' }}
            >
              Request decommission
            </Button>
            {tenant.decommissioned_at && (
              <Chip
                label={`Decommissioned ${new Date(tenant.decommissioned_at).toLocaleDateString()}${tenant.decommissioned_by ? ` by ${tenant.decommissioned_by}` : ''}`}
                style={{ marginLeft: '8px' }}
              />
            )}
          </div>

          <div>
            {editMode ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleEditToggle}
                  style={{ marginLeft: '8px' }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
              >
                Edit Tenant
              </Button>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Tenant</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete tenant "{tenant?.name}"?
            </Typography>
            <Typography color="error" style={{ marginTop: '16px' }}>
              This action will:
            </Typography>
            <ul>
              <li>Remove the tenant from the backstage database</li>
              <li>Delete all GitLab environments (staging and production)</li>
              <li>Delete all GitLab CI/CD variables for this tenant</li>
            </ul>
            <Typography color="error" style={{ fontWeight: 'bold' }}>
              This action cannot be undone!
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              color="secondary"
              variant="contained"
              disabled={deleteStatus === 'deleting'}
            >
              {deleteStatus === 'deleting' ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Request decommission: opens the tenant-ops Run pipeline form in GitLab */}
        <Dialog open={decommissionDialogOpen} onClose={() => setDecommissionDialogOpen(false)}>
          <DialogTitle>Request decommission of {tenant.name}</DialogTitle>
          <DialogContent>
            <Typography>
              This opens the tenant teardown pipeline (tenant-ops) in GitLab with this tenant filled in.
              Nothing happens until you run it there.
            </Typography>
            <ol>
              <li>In GitLab, type <b>{tenant.short_name}</b> as the value of <code>CONFIRM_TENANT</code>, then click <b>Run pipeline</b>.</li>
              <li>The inventory runs straight away. Every later step waits for a second person to approve it (Garry, George or Steve; never the person who ran the pipeline).</li>
              <li>Send the approver the pipeline link: its title names the tenant, the approval page does not.</li>
            </ol>
            <FormControlLabel
              control={
                <Switch
                  checked={decommissionDryRun}
                  onChange={e => setDecommissionDryRun(e.target.checked)}
                  color="primary"
                />
              }
              label="Dry run: report what would be removed, change nothing (do this first)"
            />
            {!decommissionDryRun && (
              <Typography color="error" style={{ fontWeight: 'bold', marginTop: '8px' }}>
                A real run stops the tenant, backs up and drops its databases, and removes its GitLab and log footprint.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDecommissionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              color={decommissionDryRun ? 'primary' : 'secondary'}
              variant="contained"
              href={teardownRunUrl(tenant.short_name, decommissionDryRun)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setDecommissionDialogOpen(false)}
            >
              {decommissionDryRun ? 'Open dry run in GitLab' : 'Open real run in GitLab'}
            </Button>
          </DialogActions>
        </Dialog>

        <Paper>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" />
            <Tab label="Profile" />
            <Tab label="Address" />
            <Tab label="Billing" />
            <Tab label="Settings" />
            <Tab label="Feature Gates" />
            <Tab label="Branding" />
            {/* <Tab label="Infrastructure" /> */}
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <InfoCard title="Basic Information">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Name</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.name}
                      onChange={e => handleInputChange('name', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.name}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Short Name</Typography>
                  <Typography variant="body1">{tenant.short_name}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Apps</Typography>
                  {editMode ? (
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', marginBottom: '8px' }}>
                        {(editedTenant.apps || []).map((app: string) => (
                          <Chip
                            key={app}
                            label={app}
                            onDelete={() => handleInputChange('apps', editedTenant.apps.filter((a: string) => a !== app))}
                          />
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          label="Add app"
                          placeholder="e.g. accountmanager"
                          helperText="Use the GitLab repo uri name (e.g. accountmanager, despatchweb)"
                          value={newAppInput}
                          onChange={e => setNewAppInput(e.target.value)}
                          onKeyPress={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' && newAppInput.trim()) {
                              handleInputChange('apps', [...(editedTenant.apps || []), newAppInput.trim()]);
                              setNewAppInput('');
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => {
                            if (newAppInput.trim()) {
                              handleInputChange('apps', [...(editedTenant.apps || []), newAppInput.trim()]);
                              setNewAppInput('');
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Typography variant="body1">{tenant.apps?.join(', ') || 'None'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Created At</Typography>
                  <Typography variant="body1">{new Date(tenant.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Updated At</Typography>
                  <Typography variant="body1">{new Date(tenant.updated_at).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <InfoCard title="Profile">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Company Name</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.profile?.company_name || ''}
                      onChange={e => handleNestedInputChange('profile', 'company_name', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.profile?.company_name || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Primary Contact Email</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.profile?.primary_contact_email || ''}
                      onChange={e => handleNestedInputChange('profile', 'primary_contact_email', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.profile?.primary_contact_email || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Phone Number</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.profile?.phone_number || ''}
                      onChange={e => handleNestedInputChange('profile', 'phone_number', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.profile?.phone_number || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Website</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.profile?.website || ''}
                      onChange={e => handleNestedInputChange('profile', 'website', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.profile?.website || 'Not set'}</Typography>
                  )}
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <InfoCard title="Address">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Street</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.address?.street || ''}
                      onChange={e => handleNestedInputChange('address', 'street', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.address?.street || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">City</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.address?.city || ''}
                      onChange={e => handleNestedInputChange('address', 'city', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.address?.city || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">State/Province</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.address?.state || ''}
                      onChange={e => handleNestedInputChange('address', 'state', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.address?.state || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Postal Code</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.address?.postal_code || ''}
                      onChange={e => handleNestedInputChange('address', 'postal_code', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.address?.postal_code || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Country</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.address?.country || ''}
                      onChange={e => handleNestedInputChange('address', 'country', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.address?.country || 'Not set'}</Typography>
                  )}
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <InfoCard title="Billing">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Billing Email</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.billing?.email || ''}
                      onChange={e => handleNestedInputChange('billing', 'email', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.billing?.email || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Billing Method</Typography>
                  {editMode ? (
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Billing Method</InputLabel>
                      <Select
                        value={editedTenant.billing?.method || ''}
                        onChange={e => handleNestedInputChange('billing', 'method', e.target.value)}
                        label="Billing Method"
                      >
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="invoice">Invoice</MenuItem>
                        <MenuItem value="direct_debit">Direct Debit</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{tenant.billing?.method || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedTenant.billing?.active || false}
                        onChange={e => handleNestedInputChange('billing', 'active', e.target.checked)}
                        disabled={!editMode}
                        color="primary"
                      />
                    }
                    label="Billing Active"
                  />
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <InfoCard title="Settings">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Time Zone</Typography>
                  {editMode ? (
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Time Zone</InputLabel>
                      <Select
                        value={editedTenant.settings?.timezone || ''}
                        onChange={e => handleNestedInputChange('settings', 'timezone', e.target.value)}
                        label="Time Zone"
                      >
                        <MenuItem value="UTC">UTC</MenuItem>
                        <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                        <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                        <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                        <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                        <MenuItem value="Pacific/Auckland">New Zealand Time</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{tenant.settings?.timezone || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Language</Typography>
                  {editMode ? (
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={editedTenant.settings?.language || ''}
                        onChange={e => handleNestedInputChange('settings', 'language', e.target.value)}
                        label="Language"
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="fr">French</MenuItem>
                        <MenuItem value="es">Spanish</MenuItem>
                        <MenuItem value="de">German</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{tenant.settings?.language || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedTenant.settings?.notifications_enabled || false}
                        onChange={e => handleNestedInputChange('settings', 'notifications_enabled', e.target.checked)}
                        disabled={!editMode}
                        color="primary"
                      />
                    }
                    label="Enable Notifications"
                  />
                </Grid>
                
                {/* Auto-deploy settings */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Auto-Deploy Settings</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedTenant.settings?.auto_deploy_staging ?? true}
                        onChange={e => handleNestedInputChange('settings', 'auto_deploy_staging', e.target.checked)}
                        disabled={!editMode}
                        color="primary"
                      />
                    }
                    label="Enable Auto-Deploy for Staging"
                  />
                  {editedTenant.settings?.auto_deploy_staging && (
                    <Typography variant="caption" display="block" style={{ marginLeft: '30px', color: 'text.secondary' }}>
                      Will use values file: {tenant.short_name}-staging-auto-deploy-values.yaml
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedTenant.settings?.auto_deploy_production ?? true}
                        onChange={e => handleNestedInputChange('settings', 'auto_deploy_production', e.target.checked)}
                        disabled={!editMode}
                        color="primary"
                      />
                    }
                    label="Enable Auto-Deploy for Production"
                  />
                  {editedTenant.settings?.auto_deploy_production && (
                    <Typography variant="caption" display="block" style={{ marginLeft: '30px', color: 'text.secondary' }}>
                      Will use values file: {tenant.short_name}-prod-auto-deploy-values.yaml
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <InfoCard title="Feature Gates">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Feature Flags</Typography>
                  <div style={{ marginTop: '16px' }}>
                    {Object.entries(tenant.feature_gates || {}).map(([feature, enabled]) => (
                      <FormControlLabel
                        key={feature}
                        control={
                          <Switch
                            checked={editMode ? editedTenant.feature_gates?.[feature] || false : enabled as boolean}
                            onChange={e => {
                              if (editMode) {
                                setEditedTenant((prev: any) => ({
                                  ...prev,
                                  feature_gates: {
                                    ...(prev.feature_gates || {}),
                                    [feature]: e.target.checked
                                  }
                                }));
                              }
                            }}
                            disabled={!editMode}
                            color="primary"
                          />
                        }
                        label={feature.replace(/_/g, ' ')}
                      />
                    ))}
                    {editMode && (
                      <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center' }}>
                        <TextField
                          label="New Feature Name"
                          size="small"
                          variant="outlined"
                          onChange={(e) => setAppInput(e.target.value)}
                          value={appInput}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ marginLeft: '8px' }}
                          onClick={() => {
                            if (appInput) {
                              handleNestedInputChange('feature_gates', appInput, false);
                              setAppInput('');
                            }
                          }}
                        >
                          Add Feature
                        </Button>
                      </div>
                    )}
                  </div>
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <InfoCard title="Branding">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Primary Color</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.branding?.primary_color || ''}
                      onChange={e => handleNestedInputChange('branding', 'primary_color', e.target.value)}
                      margin="normal"
                      variant="outlined"
                      placeholder="#RRGGBB"
                    />
                  ) : (
                    <div>
                      <Typography variant="body1">{tenant.branding?.primary_color || 'Not set'}</Typography>
                      {tenant.branding?.primary_color && (
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          backgroundColor: tenant.branding.primary_color,
                          display: 'inline-block',
                          marginLeft: '8px',
                          border: '1px solid #ccc'
                        }}></div>
                      )}
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Secondary Color</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.branding?.secondary_color || ''}
                      onChange={e => handleNestedInputChange('branding', 'secondary_color', e.target.value)}
                      margin="normal"
                      variant="outlined"
                      placeholder="#RRGGBB"
                    />
                  ) : (
                    <div>
                      <Typography variant="body1">{tenant.branding?.secondary_color || 'Not set'}</Typography>
                      {tenant.branding?.secondary_color && (
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          backgroundColor: tenant.branding.secondary_color,
                          display: 'inline-block',
                          marginLeft: '8px',
                          border: '1px solid #ccc'
                        }}></div>
                      )}
                    </div>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Logo URL</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.branding?.logo_url || ''}
                      onChange={e => handleNestedInputChange('branding', 'logo_url', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <div>
                      <Typography variant="body1">{tenant.branding?.logo_url || 'Not set'}</Typography>
                      {tenant.branding?.logo_url && (
                        <img 
                          src={tenant.branding.logo_url} 
                          alt="Tenant Logo" 
                          style={{ maxHeight: '60px', marginTop: '8px' }}
                        />
                      )}
                    </div>
                  )}
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel>

          {/* <TabPanel value={tabValue} index={7}>
            <InfoCard title="Infrastructure">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Region</Typography>
                  {editMode ? (
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Region</InputLabel>
                      <Select
                        value={editedTenant.infrastructure?.region || ''}
                        onChange={e => handleNestedInputChange('infrastructure', 'region', e.target.value)}
                        label="Region"
                      >
                        <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                        <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                        <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                        <MenuItem value="ap-southeast-2">Asia Pacific (Sydney)</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{tenant.infrastructure?.region || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Instance Type</Typography>
                  {editMode ? (
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel>Instance Type</InputLabel>
                      <Select
                        value={editedTenant.infrastructure?.instance_type || ''}
                        onChange={e => handleNestedInputChange('infrastructure', 'instance_type', e.target.value)}
                        label="Instance Type"
                      >
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="performance">Performance</MenuItem>
                        <MenuItem value="enterprise">Enterprise</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{tenant.infrastructure?.instance_type || 'Not set'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Hostname</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editedTenant.infrastructure?.hostname || ''}
                      onChange={e => handleNestedInputChange('infrastructure', 'hostname', e.target.value)}
                      margin="normal"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1">{tenant.infrastructure?.hostname || 'Not set'}</Typography>
                  )}
                </Grid>
              </Grid>
            </InfoCard>
          </TabPanel> */}
        </Paper>
      </Content>
    </Page>
  );
};
